import React, { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  SparkleIcon,
  FilmReelIcon,
  FireIcon,
  HandCoinsIcon,
  LightningIcon,
  MagicWandIcon,
} from "@phosphor-icons/react";

/**
 * In-stream Plus upgrade sheet — opens over the live show so the seller
 * (or buyer) never has to leave the broadcast to subscribe.  We hit
 * `POST /api/plus/checkout` for a hosted Stripe URL and open it in a
 * **new browser tab** instead of redirecting; the seller's WebRTC /
 * Mux pipeline keeps streaming in the original tab.
 *
 * Props:
 *   open       — boolean
 *   onClose    — () => void
 *   reason     — optional copy line above the perks (e.g. "Unlock Cinema 4K")
 */
const PERKS = [
  { icon: FilmReelIcon,   text: "Cinema 4K — true broadcast pipeline." },
  { icon: MagicWandIcon,  text: "Auto-Detect & List — show an item on camera, Bidoria spots it in 2s." },
  { icon: SparkleIcon,    text: "Bidoria AI co-host on every stream." },
  { icon: HandCoinsIcon,  text: "1% cashback credits on every purchase." },
  { icon: FireIcon,       text: "2× streak rewards ($10 / $50 milestones)." },
  { icon: LightningIcon,  text: "Early access — join scheduled shows 5 min early." },
];

export default function PlusUpgradeSheet({ open, onClose, reason, lockYearly }) {
  const [plan, setPlan] = useState(lockYearly ? "yearly" : "yearly");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (lockYearly) setPlan("yearly");
  }, [lockYearly]);

  const onSubscribe = async () => {
    setBusy(true);
    try {
      const r = await api.post("/plus/checkout", {
        plan,
        origin_url: window.location.origin,
      });
      const url = r?.data?.url;
      if (!url) {
        toast.error("Could not start checkout");
        return;
      }
      // Open in a new tab so the live show keeps streaming in the
      // original tab.  Falls back to a same-tab navigate if the
      // popup is blocked (rare on user-gestured clicks).
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        window.location.href = url;
      } else {
        toast.success("Checkout opened in a new tab. You can keep watching here.");
        onClose && onClose();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl md:rounded-2xl md:max-w-[460px] md:mx-auto md:my-auto md:inset-x-0 pb-[max(env(safe-area-inset-bottom,0px),96px)] md:pb-8 bg-zinc-950 text-white border-t border-white/10"
        data-testid="plus-upgrade-sheet"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="text-white font-display font-black text-2xl tracking-tight flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gradient-to-br from-amber-300 via-amber-400 to-orange-500 text-zinc-950 text-xs font-black tracking-wide">
              <SparkleIcon size={12} weight="fill" /> PLUS
            </span>
            Unlock more on Bidorah
          </SheetTitle>
          {reason && (
            <p className="text-sm text-amber-300/90 mt-1">{reason}</p>
          )}
          {lockYearly && (
            <p className="text-[11px] uppercase tracking-[0.18em] text-amber-200/80 font-black mt-2">
              For sellers who want to list 10× faster
            </p>
          )}
        </SheetHeader>

        <ul className="mt-4 space-y-2.5">
          {PERKS.map(({ icon: Icon, text }, i) => (
            <li
              key={i}
              className="flex items-start gap-2.5 text-sm text-zinc-200"
              data-testid={`plus-perk-${i}`}
            >
              <span className="h-6 w-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 grid place-items-center flex-shrink-0">
                <Icon size={13} weight="fill" className="text-zinc-950" />
              </span>
              <span className="leading-snug">{text}</span>
            </li>
          ))}
        </ul>

        <div
          className="mt-5 grid grid-cols-2 gap-2 rounded-2xl border border-white/10 p-1.5 bg-zinc-900/60"
          data-testid="plus-plan-toggle"
        >
          <button
            type="button"
            onClick={() => !lockYearly && setPlan("monthly")}
            disabled={lockYearly}
            className={`py-2.5 rounded-xl text-sm font-bold transition ${
              plan === "monthly"
                ? "bg-white text-zinc-950"
                : "text-zinc-300 hover:text-white"
            } ${lockYearly ? "opacity-40 cursor-not-allowed" : ""}`}
            data-testid="plus-plan-monthly"
            title={lockYearly ? "This feature ships only on the yearly plan" : ""}
          >
            Monthly · $9.99
          </button>
          <button
            type="button"
            onClick={() => setPlan("yearly")}
            className={`relative py-2.5 rounded-xl text-sm font-bold transition ${
              plan === "yearly"
                ? "bg-white text-zinc-950"
                : "text-zinc-300 hover:text-white"
            }`}
            data-testid="plus-plan-yearly"
          >
            Yearly · $79
            <span className="absolute -top-2 right-2 text-[9px] font-black tracking-wider px-1.5 py-0.5 rounded-full bg-gradient-to-br from-amber-300 to-orange-500 text-zinc-950">
              SAVE 34%
            </span>
          </button>
        </div>

        <Button
          type="button"
          onClick={onSubscribe}
          disabled={busy}
          className="mt-4 w-full h-12 rounded-full bg-gradient-to-r from-amber-300 via-amber-400 to-orange-500 hover:from-amber-200 hover:to-orange-400 text-zinc-950 font-black tracking-tight text-base shadow-lg"
          data-testid="plus-subscribe-btn"
        >
          {busy ? "Opening checkout…" : `Continue · ${plan === "yearly" ? "$79/yr" : "$9.99/mo"}`}
        </Button>

        <p className="mt-2 text-[11px] text-zinc-500 text-center">
          Secure checkout via Stripe · cancel anytime · keeps the live show running.
        </p>
      </SheetContent>
    </Sheet>
  );
}
